import Service from "@/service/api.js";
import { setItem, getItem, removeItem } from '@/utils/db';
import router from '@/router/index.js';

const STORAGE_KEYS = {
    ACCESS_TOKEN: "accessToken",
    USER: "user",
    LOGIN_SOURCE: "loginSource",
};

function setAxiosAuthHeader(token) {
    // Ensure your Service layer uses this (e.g., axios.defaults.headers.common)
    try {
        Service.setAuthToken?.(token); // if you exposed a helper in Service
    } catch (_) {}
}

const state = {
    authenticated: {
        isAuthen: !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
        isOAuth: false,
        user: JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || "null"),
        loginSource: localStorage.getItem(STORAGE_KEYS.LOGIN_SOURCE) || "", // 'student'|'admin'|''
    },
    loginError: "",
};

const mutations = {
    SET_TOKEN(state, token) {
        if (token) {
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
            state.authenticated.isAuthen = true;
            setAxiosAuthHeader(token);
        } else {
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            state.authenticated.isAuthen = false;
            setAxiosAuthHeader(null);
        }
    },
    SET_USER(state, user) {
        state.authenticated.user = user;
        state.authenticated.isOAuth = !!user?.google;
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    },
    SET_LOGIN_SOURCE(state, src) {
        state.authenticated.loginSource = src || "";
        if (src) localStorage.setItem(STORAGE_KEYS.LOGIN_SOURCE, src);
        else localStorage.removeItem(STORAGE_KEYS.LOGIN_SOURCE);
    },
    CLEAR_USER(state) {
        state.authenticated.user = null;
        state.authenticated.isOAuth = false;
    },
    SET_LOGIN_ERROR(state, message) {
        state.loginError = message || "";
    },
};

const actions = {
    // Call once on app boot (e.g., in main.js)
    initAuth({ commit }) {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const user = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || "null");
        const loginSource = localStorage.getItem(STORAGE_KEYS.LOGIN_SOURCE) || "";

        if (token) setAxiosAuthHeader(token);
        commit("SET_TOKEN", token || null);
        if (user) commit("SET_USER", user);
        commit("SET_LOGIN_SOURCE", loginSource);
    },

    async signInWithGoogle({ commit, dispatch }, { token, loginSource }) {
        commit("SET_LOGIN_ERROR", "");

        if (!token) {
            commit("SET_LOGIN_ERROR", "Google login failed.");
            return;
        }

        try {
            const res = await Service.authenticated("signin", { token, loginSource });
            const { accessToken, data: user } = res.data || {};

            if (!accessToken || !user) {
                commit("SET_LOGIN_ERROR", "Sign-in response incomplete.");
                return;
            }

            commit("SET_TOKEN", accessToken);
            commit("SET_USER", user);
            commit("SET_LOGIN_SOURCE", loginSource);

            const email = user.email || "";
            const roleName = user.role?.role_name || "";

            const validEmail =
                email.endsWith("@lamduan.mfu.ac.th") || email.endsWith("@mfu.ac.th");

            if (loginSource === "student") {
                if (validEmail) {
                    router.push("/home");
                } else {
                    commit("SET_LOGIN_ERROR", "Student login requires Lamduan mail.");
                    await dispatch("signOut");
                }
                return;
            }

            if (loginSource === "admin") {
                const validRole = ["superadmin", "admin", "committee"].includes(roleName);
                if (validEmail && validRole) {
                    if (roleName === "committee") router.push("/committee/committee_home");
                    else router.push("/admin/admin_home");
                } else {
                    commit(
                        "SET_LOGIN_ERROR",
                        "Your account does not match. Please try again."
                    );
                    await dispatch("signOut");
                }
                return;
            }

            // Fallback
            commit("SET_LOGIN_ERROR", "Invalid login source.");
            router.push("/login");
        } catch (err) {
            commit(
                "SET_LOGIN_ERROR",
                err?.response?.data?.message || "Google Sign-In failed."
            );
            // keep state clean on failure
            await removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            await removeItem(STORAGE_KEYS.USER);
            localStorage.removeItem(STORAGE_KEYS.LOGIN_SOURCE);
        }
    },

    // Use after refresh if you need role-based landing
    handleRoleRedirect({ state }) {
        const role = state.authenticated.user?.role?.role_name;
        switch (role) {
            case "superadmin":
            case "admin":
                router.push("/admin/admin_home");
                break;
            case "committee":
                router.push("/committee/committee_home");
                break;
            case "user":
            case "candidate":
                router.push("/home");
                break;
            default:
                router.push("/login");
        }
    },

    async signOut({ commit }) {
        await removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        await removeItem(STORAGE_KEYS.USER);
        localStorage.removeItem(STORAGE_KEYS.LOGIN_SOURCE);
        commit("SET_TOKEN", null);
        commit("CLEAR_USER");
        commit("SET_LOGIN_SOURCE", "");
        router.push("/login");
    },
};

const getters = {
    user: (s) => s.authenticated.user,
    isAuthen: (s) => s.authenticated.isAuthen,
    loginSource: (s) => s.authenticated.loginSource,
    roleName: (s) => s.authenticated.user?.role?.role_name || "",
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};